import axios from "axios";

const baseURL = "https://volumaire.backendapihub.com";

const axiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.method === 'get') {
      config.headers['Cache-Control'] = 'no-cache';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 404) {
      return Promise.reject(error);
    }

    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        message: "Request timeout. Please check your internet connection and try again.",
      });
    }

    if (!error.response) {
      return Promise.reject({
        message: "Network error. Please check your internet connection and try again.",
      });
    }

    const { status, data } = error.response;

    switch (status) {
      case 401:
        localStorage.removeItem("token");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("userEmail");
        localStorage.removeItem("userName");
        localStorage.removeItem("userData");
        sessionStorage.removeItem("token");
        return Promise.reject({
          message: data.message || "Session expired. Please login again.",
          ...data
        });
      case 403:
        return Promise.reject({
          message: "You don't have permission to access this resource.",
          ...data
        });
      case 422:
        return Promise.reject({
          message: data.message || "Validation error.",
          errors: data.errors,
          ...data
        });
      case 500:
        return Promise.reject({
          message: "Server error. Please try again later.",
          ...data
        });
      default:
        break;
    }

    return Promise.reject(data || {
      message: "An unexpected error occurred. Please try again.",
    });
  }
);

// ========== CACHE & REQUEST DEDUPLICATION ==========
const responseCache = new Map();
const pendingRequests = new Map();

const generateKey = (method, url, params = {}, data = null) => {
  const normalizeObject = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const sorted = Object.keys(obj)
      .filter(key => obj[key] !== undefined)
      .sort()
      .reduce((acc, key) => {
        acc[key] = obj[key];
        return acc;
      }, {});
    return Object.keys(sorted).length ? sorted : '{}';
  };

  const normalizedParams = normalizeObject(params);
  const paramsString = JSON.stringify(normalizedParams);

  if (method.toLowerCase() === 'get' || !data) {
    return `${method}_${url}_${paramsString}`;
  }

  const normalizedData = normalizeObject(data);
  const dataString = JSON.stringify(normalizedData);
  return `${method}_${url}_${paramsString}_${dataString}`;
};

const apiCaller = async (method, url, data = null, params = {}, options = {}) => {
  const { useCache = true, skipCache = false, deduplicate = true } = options;
  const key = generateKey(method, url, params, method.toLowerCase() !== 'get' ? data : null);

  if (deduplicate && pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  if (method.toLowerCase() === 'get' && useCache && !skipCache && responseCache.has(key)) {
    return Promise.resolve(responseCache.get(key));
  }

  const promise = (async () => {
    try {
      let res;
      const config = {
        params,
        headers: { 'Content-Type': 'application/json' }
      };

      const token = localStorage.getItem('token');
      if (token) config.headers.Authorization = `Bearer ${token}`;

      console.log(`🌐 API Call: ${method.toUpperCase()} ${url}`, { 
        data: method.toLowerCase() !== 'get' ? data : undefined, 
        params 
      });

      switch (method.toLowerCase()) {
        case 'get':    res = await axiosInstance.get(url, config); break;
        case 'post':   res = await axiosInstance.post(url, data, config); break;
        case 'put':    res = await axiosInstance.put(url, data, config); break;
        case 'patch':  res = await axiosInstance.patch(url, data, config); break;
        case 'delete': res = await axiosInstance.delete(url, config); break;
        default: throw new Error(`Unsupported method: ${method}`);
      }

      console.log(`✅ API Response: ${method.toUpperCase()} ${url}`, res.data);

      if (method.toLowerCase() === 'get' && useCache && !skipCache) {
        responseCache.set(key, res.data);
      }

      return res.data;
    } catch (error) {
      if (error.response?.status === 404) {
        console.warn(`⚠️ API Warning (404): ${method.toUpperCase()} ${url} – Endpoint not found, using fallback if available.`);
      } else {
        console.error(`❌ API Error: ${method.toUpperCase()} ${url}`, error.response?.data || error.message);
      }
      throw error;
    } finally {
      pendingRequests.delete(key);
    }
  })();

  if (deduplicate) {
    pendingRequests.set(key, promise);
  }

  return promise;
};

export const clearCache = () => {
  responseCache.clear();
  pendingRequests.clear();
};

export const removeFromCache = (method, url, params = {}, data = null) => {
  const key = generateKey(method, url, params, method.toLowerCase() !== 'get' ? data : null);
  responseCache.delete(key);
  pendingRequests.delete(key);
};

// ========== ALL API FUNCTIONS ==========

// --- CONTACT FORM ---
export const submitContactForm = async (contactData) => {
  try {
    if (!contactData.email || !contactData.name || !contactData.message) {
      return { success: false, message: 'Name, Email and Message are required fields' };
    }
    const payload = {
      name: contactData.name.trim(),
      email: contactData.email.trim().toLowerCase(),
      message: contactData.message.trim(),
    };
    if (contactData.phone && contactData.phone.trim()) payload.phone = contactData.phone.trim();

    const response = await apiCaller('post', '/contact', payload, {}, { useCache: false });
    return {
      success: true,
      message: response.message || 'Thank you! We will get back to you soon.',
      data: response
    };
  } catch (error) {
    console.error('Contact form submission error:', error);
    throw error;
  }
};

export const testContactAPI = async () => {
  const testData = {
    name: "Test User",
    email: "test@example.com",
    message: "Test message from debug"
  };
  try {
    const response = await fetch('https://volumaire.backendapihub.com/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(testData)
    });
    const data = await response.json();
    console.log('Test API Response:', { status: response.status, data });
    return data;
  } catch (error) {
    console.error('Test API Error:', error);
    return null;
  }
};

// --- PAYMENT ---
export const initiatePaymentAPI = async (paymentData) => {
  try {
    console.log(" [initiatePaymentAPI] Sending to /api/payment/initiate:", paymentData);
    const response = await apiCaller('post', '/api/payment/initiate', paymentData, {}, { useCache: false });
    console.log(" [initiatePaymentAPI] Response:", response);
    return response;
  } catch (error) {
    console.error(" [initiatePaymentAPI] Error:", error);
    throw error;
  }
};

export const verifyPaymentAPI = async (verificationData) => {
  try {
    console.log(" [verifyPaymentAPI] Verifying payment with:", verificationData);
    
    const params = {};
    const postData = {};
    
    if (verificationData.transactionId) {
      params.transactionId = verificationData.transactionId;
      postData.transactionId = verificationData.transactionId;
    }
    
    if (verificationData.orderId) {
      params.orderId = verificationData.orderId;
      postData.orderId = verificationData.orderId;
    }
    
    if (verificationData.txnid) {
      params.txnid = verificationData.txnid;
      postData.txnid = verificationData.txnid;
    }
    
    if (verificationData.mihpayid) {
      params.mihpayid = verificationData.mihpayid;
      postData.mihpayid = verificationData.mihpayid;
    }
    
    if (verificationData.status) {
      params.status = verificationData.status;
      postData.status = verificationData.status;
    }
    
    if (verificationData.paymentMethod) {
      postData.paymentMethod = verificationData.paymentMethod;
    }
    
    if (verificationData.allParams) {
      postData.allParams = verificationData.allParams;
    }
    
    console.log(" [verifyPaymentAPI] Query params:", params);
    
    try {
      const response = await apiCaller('post', '/api/payment/verify', postData, {}, { useCache: false });
      console.log(" [verifyPaymentAPI] POST Response:", response);
      return response;
    } catch (postError) {
      console.log(" [verifyPaymentAPI] POST failed, trying GET:", postError);
      const response = await apiCaller('get', '/api/payment/verify', null, params, { useCache: false });
      console.log(" [verifyPaymentAPI] GET Response:", response);
      return response;
    }
  } catch (error) {
    console.error(" [verifyPaymentAPI] Error:", error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Payment verification failed"
    };
  }
};

// --- AUTH (FIXED) ---
export const registerUser = async (data) => {
  try {
    console.log("Registering user with data:", data);
    const response = await apiCaller('post', '/api/auth/register', data);

    // If server returns an array (unexpected), convert to error object
    if (Array.isArray(response)) {
      console.error("Unexpected array response from server:", response);
      return {
        success: false,
        message: "Server returned an unexpected format. Please try again.",
        data: response
      };
    }

    // If empty object (maybe 200 with no content), treat as success requiring verification
    if (response && typeof response === 'object' && Object.keys(response).length === 0) {
      return {
        success: true,
        message: "Registration successful! Please check your email to verify your account."
      };
    }

    // If response already has a 'success' property, return it as is
    if (response && typeof response === 'object' && 'success' in response) {
      return response;
    }

    // Otherwise, assume success and wrap
    return {
      success: true,
      message: response?.message || "Registration successful!",
      data: response
    };
  } catch (error) {
    console.error("Registration API error:", error);
    // Re-throw to be handled by the component
    throw error;
  }
};

export const loginUser = async (data) => {
  try {
    const response = await apiCaller('post', '/api/auth/login', data);

    if (Array.isArray(response)) {
      return {
        success: false,
        message: "Unexpected server response. Please try again."
      };
    }

    // If response already has success field, return it
    if (response && typeof response === 'object' && 'success' in response) {
      return response;
    }

    // Otherwise assume success if token exists
    if (response?.token) {
      return {
        success: true,
        message: "Login successful",
        token: response.token,
        data: response.data || response
      };
    }

    return {
      success: false,
      message: response?.message || "Login failed. Please check your credentials."
    };
  } catch (error) {
    console.error("Login API error:", error);
    throw error;
  }
};

export const verifyEmailToken = async (token) => {
  try {
    const response = await apiCaller('get', '/api/auth/verify-email', null, { token });

    if (Array.isArray(response)) {
      return {
        success: false,
        message: "Verification failed due to server error."
      };
    }

    // If empty object, assume success but no data
    if (response && typeof response === 'object' && Object.keys(response).length === 0) {
      return {
        success: true,
        message: "Email verified successfully! You can now login."
      };
    }

    // If response already has success property, return it
    if (response && typeof response === 'object' && 'success' in response) {
      return response;
    }

    // Otherwise, if token is present, treat as success
    if (response?.token) {
      return {
        success: true,
        message: response.message || "Email verified successfully!",
        token: response.token,
        data: response.data || response
      };
    }

    // Fallback: assume success with message from response
    return {
      success: true,
      message: response?.message || "Email verified successfully!",
      data: response
    };
  } catch (error) {
    console.error("Email verification error:", error);
    throw error;
  }
};

// NEW: Resend verification email
export const resendVerificationEmail = async (email) => {
  try {
    const response = await apiCaller('post', '/api/auth/resend-verification', { email });
    if (Array.isArray(response)) {
      return { success: false, message: "Unexpected server response." };
    }
    if (response && typeof response === 'object' && 'success' in response) {
      return response;
    }
    return { success: true, message: response?.message || "Verification email sent." };
  } catch (error) {
    console.error("Resend verification error:", error);
    throw error;
  }
};

export const sendMobileOtp = async (data) => {
  try {
    return await apiCaller('post', '/api/auth/send-otp', { phone: data.mobile });
  } catch (error) {
    console.error("Send OTP error:", error);
    throw error;
  }
};

export const verifyMobileOtp = async (data) => {
  try {
    return await apiCaller('post', '/api/auth/verify-otp', { phone: data.mobile, otp: data.otp });
  } catch (error) {
    console.error("Verify OTP error:", error);
    throw error;
  }
};

export const forgotPassword = async (email) => {
  try {
    return await apiCaller('post', '/api/auth/forgot-password', { email });
  } catch (error) {
    console.error("Forgot password error:", error);
    throw error;
  }
};

export const resetPassword = async (data) => {
  try {
    return await apiCaller('post', '/api/auth/reset-password', data);
  } catch (error) {
    console.error("Reset password error:", error);
    throw error;
  }
};

// --- ORDERS (unchanged) ---
const extractOrderId = (response) => {
  if (!response) return null;
  if (response._id) return response._id;
  if (response.id) return response.id;
  if (response.orderId) return response.orderId;
  if (response.data) {
    if (response.data._id) return response.data._id;
    if (response.data.id) return response.data.id;
    if (response.data.orderId) return response.data.orderId;
    if (response.data.order && response.data.order._id) return response.data.order._id;
  }
  if (response.order) {
    if (response.order._id) return response.order._id;
    if (response.order.id) return response.order.id;
    if (response.order.orderId) return response.order.orderId;
  }
  return null;
};

export const createOrder = async (orderData) => {
  if (!orderData.items || orderData.items.length === 0) {
    throw new Error('Missing required order data: items');
  }

  let payload = { ...orderData };

  if (!payload.shippingAddress) {
    const hasFlatFields = 
      payload.shippingStreet && 
      payload.shippingCity && 
      payload.shippingState && 
      payload.shippingZipCode &&
      payload.shippingCountry &&
      payload.shippingPhone &&
      payload.shippingEmail &&
      payload.shippingLastName;

    if (hasFlatFields) {
      payload.shippingAddress = {
        street: payload.shippingStreet,
        city: payload.shippingCity,
        state: payload.shippingState,
        zipCode: payload.shippingZipCode,
        country: payload.shippingCountry,
        phone: payload.shippingPhone,
        email: payload.shippingEmail,
        firstName: payload.shippingFirstName || '',
        lastName: payload.shippingLastName,
        address2: payload.shippingAddress2 || ''
      };
    } else {
      throw new Error('Missing shipping information: provide either shippingAddress object or all flat shipping fields');
    }
  }

  if (payload.shippingAddress) {
    payload = {
      ...payload,
      shippingStreet: payload.shippingAddress.street || payload.shippingStreet || '',
      shippingZipCode: payload.shippingAddress.zipCode || payload.shippingZipCode || '',
      shippingCity: payload.shippingAddress.city || payload.shippingCity || '',
      shippingState: payload.shippingAddress.state || payload.shippingState || '',
      shippingCountry: payload.shippingAddress.country || payload.shippingCountry || 'India',
      shippingPhone: payload.shippingAddress.phone || payload.shippingPhone || '',
      shippingEmail: payload.shippingAddress.email || payload.shippingEmail || '',
      shippingFirstName: payload.shippingAddress.firstName || payload.shippingFirstName || '',
      shippingLastName: payload.shippingAddress.lastName || payload.shippingLastName || '',
      shippingAddress2: payload.shippingAddress.address2 || payload.shippingAddress2 || ''
    };
  }

  const validatedItems = orderData.items.map(item => {
    let selectedAttributes = item.selectedAttributes || [];
    if (typeof selectedAttributes === 'string') selectedAttributes = [selectedAttributes];
    if (!Array.isArray(selectedAttributes)) selectedAttributes = [];

    return {
      productId: String(item.productId || ''),
      productName: item.productName || 'Product',
      price: Number(item.price) || 0,
      quantity: Number(item.quantity) || 1,
      image: item.image || '',
      selectedAttributes: selectedAttributes,
      selectedAttributeDetails: item.selectedAttributeDetails || []
    };
  });

  const finalPayload = {
    ...payload,
    items: validatedItems,
    subtotal: Number(payload.subtotal || 0),
    shipping: Number(payload.shipping || 0),
    tax: Number(payload.tax || 0),
    total: Number(payload.total || 0),
    paymentMethod: payload.paymentMethod || 'cod',
    paymentStatus: payload.paymentStatus || 'pending',
    orderStatus: payload.orderStatus || 'pending',
    cardLast4: payload.cardLast4 ? String(payload.cardLast4).slice(-4) : ''
  };

  try {
    const response = await apiCaller('post', '/api/orders', finalPayload);
    const orderId = extractOrderId(response);
    if (!orderId) {
      console.warn('Order created but no ID found in response. Raw response:', response);
    } else if (!response._id) {
      response._id = orderId;
    }
    return response;
  } catch (error) {
    console.error('Order creation failed:', error);
    throw error;
  }
};

export const getOrders = async (params = {}, options = {}) => {
  try {
    return await apiCaller('get', '/api/orders/my-orders', null, params, options);
  } catch (error) {
    console.error('Get orders failed:', error);
    throw error;
  }
};

export const getOrderById = async (orderId, options = {}) => {
  try {
    const response = await apiCaller('get', `/api/orders/${orderId}`, null, {}, options);
    const extractedId = extractOrderId(response);
    if (extractedId && !response._id) response._id = extractedId;
    return response;
  } catch (error) {
    console.error(`Get order ${orderId} failed:`, error);
    throw error;
  }
};

// --- PRODUCTS (unchanged) ---
export const getCompleteProducts = async (params = {}, options = {}) => {
  try {
    const processedParams = {
      page: params.page || 1,
      limit: params.limit || 10,
      categoryId: params.categoryId || undefined,
      subcategoryId: params.subcategoryId || undefined,
      search: params.search || "",
      isActive: params.isActive ?? true,
      bestseller: params.bestseller || undefined,
    };
    return await apiCaller('get', '/api/complete-products', null, processedParams, options);
  } catch (error) {
    throw error;
  }
};

export const getProductById = async (productId, options = {}) => {
  try {
    return await apiCaller('get', `/api/complete-products/${productId}`, null, {}, options);
  } catch (error) {
    throw error;
  }
};

export const getCompleteProduct = getProductById;

// ========== PRODUCT SUBSECTIONS API ==========
export const getProductSubsections = async (productId, options = {}) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    
    console.log(`Fetching subsections for product: ${productId}`);
    
    const response = await apiCaller('get', `/api/products/${productId}/subsections`, null, {}, {
      useCache: true,
      skipCache: false,
      ...options
    });
    
    if (response && response.success === true && response.data) {
      return { success: true, data: response.data };
    } else if (response && response.data) {
      return { success: true, data: response.data };
    } else if (response && typeof response === 'object') {
      return { success: true, data: response };
    }
    
    return { success: true, data: {} };
  } catch (error) {
    console.error(`Error fetching subsections for product ${productId}:`, error);
    return { 
      success: false, 
      data: {}, 
      error: error.message || 'Failed to fetch product subsections'
    };
  }
};

export const getBestsellerProducts = async (options = {}) => {
  try {
    return await apiCaller('get', '/api/complete-products/bestsellers', null, {}, options);
  } catch (error) {
    throw error;
  }
};

export const getBestsellerProductsByQuery = async (options = {}) => {
  try {
    return await apiCaller('get', '/api/complete-products', null, { bestseller: true, limit: 8 }, options);
  } catch (error) {
    throw error;
  }
};

export const searchProducts = async (search, options = {}) => {
  try {
    return await apiCaller('get', '/api/complete-products', null, { search }, options);
  } catch (error) {
    throw error;
  }
};

// --- CATEGORIES (unchanged) ---
export const getCategories = async (options = {}) => {
  try {
    return await apiCaller('get', '/api/categories', null, {}, options);
  } catch (error) {
    throw error;
  }
};

export const getSubCategories = async (options = {}) => {
  try {
    return await apiCaller('get', '/api/subcategories', null, {}, options);
  } catch (error) {
    throw error;
  }
};

// --- USER PROFILE (unchanged) ---
export const getUserProfile = async (options = {}) => {
  try {
    return await apiCaller('get', '/api/user/profile', null, {}, options);
  } catch (error) {
    throw error;
  }
};

export const updateUserProfile = async (data) => {
  try {
    return await apiCaller('put', '/api/user/profile', data);
  } catch (error) {
    throw error;
  }
};

// --- USER ADDRESSES (unchanged) ---
export const getUserAddresses = async (options = {}) => {
  try {
    return await apiCaller('get', '/api/usersaddresses', null, {}, options);
  } catch (error) {
    console.error('Get user addresses failed:', error);
    throw error;
  }
};

export const addUserAddress = async (addressData) => {
  try {
    return await apiCaller('post', '/api/usersaddresses', addressData);
  } catch (error) {
    console.error('Add user address failed:', error);
    throw error;
  }
};

export const updateUserAddress = async (id, addressData) => {
  try {
    return await apiCaller('put', `/api/usersaddresses/${id}`, addressData);
  } catch (error) {
    console.error('Update user address failed:', error);
    throw error;
  }
};

export const deleteUserAddress = async (id) => {
  try {
    return await apiCaller('delete', `/api/usersaddresses/${id}`);
  } catch (error) {
    console.error('Delete user address failed:', error);
    throw error;
  }
};

// ========== WISHLIST FUNCTIONS (unchanged) ==========

const getProperCountryCode = () => {
  try {
    const savedCountry = localStorage.getItem('selectedCountry');
    if (savedCountry) {
      if (savedCountry.startsWith('{')) {
        const countryObj = JSON.parse(savedCountry);
        const code = countryObj.countryCode || countryObj.code;
        if (code === 'IND') return 'IN';
        if (code === 'USA') return 'US';
        return code || 'US';
      } else {
        const code = savedCountry;
        if (code === 'IND') return 'IN';
        if (code === 'USA') return 'US';
        return code || 'US';
      }
    }
  } catch (error) {
    console.log('Error parsing country code:', error);
  }
  return 'US';
};

export const getWishlistAPI = async (options = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log("No token found for wishlist API");
      return { success: true, data: [] };
    }
    
    const countryCode = getProperCountryCode();
    
    const params = {
      countryCode: countryCode,
      ...options.params
    };
    
    console.log('Fetching wishlist with params:', params);
    
    try {
      const response = await apiCaller('get', '/api/wishlists', null, params, { 
        ...options,
        skipCache: true
      });
      
      console.log('Wishlist API response:', response);
      
      let wishlistData = [];
      if (response && response.success === true && response.data) {
        wishlistData = response.data;
      } else if (response && response.data) {
        wishlistData = response.data;
      } else if (Array.isArray(response)) {
        wishlistData = response;
      } else if (response && typeof response === 'object') {
        if (response._id || response.id) {
          wishlistData = [response];
        }
      }
      
      if (wishlistData.length > 0) {
        localStorage.setItem('wishlistItems', JSON.stringify(wishlistData));
      }
      
      return { success: true, data: wishlistData };
    } catch (apiError) {
      console.log('Wishlist API error, using localStorage:', apiError.message);
      
      const savedWishlist = localStorage.getItem('wishlistItems');
      const localData = savedWishlist ? JSON.parse(savedWishlist) : [];
      
      return { 
        success: true, 
        data: localData,
        fromLocalStorage: true
      };
    }
  } catch (error) {
    console.error('Wishlist API error:', error);
    return { success: true, data: [] };
  }
};

export const addToWishlistAPI = async (productId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Please login to add to wishlist');
    }
    
    const countryCode = getProperCountryCode();
    
    const payload = {
      productId,
      countryCode: countryCode
    };
    
    console.log('Adding to wishlist with payload:', payload);
    
    try {
      const response = await apiCaller('post', '/api/wishlists', payload, {}, { useCache: false });
      console.log('Add to wishlist response:', response);
      
      const savedWishlist = localStorage.getItem('wishlistItems');
      let wishlist = savedWishlist ? JSON.parse(savedWishlist) : [];
      
      const exists = wishlist.some(item => String(item.productId) === String(productId));
      
      if (!exists) {
        const newItem = response.data || response || {
          id: response?._id || `api_${Date.now()}`,
          productId: productId
        };
        wishlist.push(newItem);
        localStorage.setItem('wishlistItems', JSON.stringify(wishlist));
      }
      
      return { success: true, data: response };
    } catch (apiError) {
      console.log('Add to wishlist API failed, using localStorage:', apiError.message);
      
      const savedWishlist = localStorage.getItem('wishlistItems');
      let wishlist = savedWishlist ? JSON.parse(savedWishlist) : [];
      
      const exists = wishlist.some(item => String(item.productId) === String(productId));
      
      if (!exists) {
        const newItem = {
          id: `local_${Date.now()}`,
          productId: productId,
          createdAt: new Date().toISOString()
        };
        wishlist.push(newItem);
        localStorage.setItem('wishlistItems', JSON.stringify(wishlist));
        
        return { 
          success: true, 
          data: newItem,
          fromLocalStorage: true
        };
      }
      
      return { success: true, data: wishlist };
    }
  } catch (error) {
    console.log('Add to wishlist failed:', error);
    throw error;
  }
};

export const removeFromWishlistAPI = async (wishlistItemId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Please login to remove from wishlist');
    }
    
    try {
      const response = await apiCaller('delete', `/api/wishlists/${wishlistItemId}`, null, {}, { useCache: false });
      
      const savedWishlist = localStorage.getItem('wishlistItems');
      if (savedWishlist) {
        let wishlist = JSON.parse(savedWishlist);
        wishlist = wishlist.filter(item => 
          String(item.id) !== String(wishlistItemId) && 
          String(item._id) !== String(wishlistItemId)
        );
        localStorage.setItem('wishlistItems', JSON.stringify(wishlist));
      }
      
      return { success: true, data: response };
    } catch (apiError) {
      console.log('Remove from wishlist API failed, using localStorage:', apiError.message);
      
      const savedWishlist = localStorage.getItem('wishlistItems');
      if (savedWishlist) {
        let wishlist = JSON.parse(savedWishlist);
        wishlist = wishlist.filter(item => 
          String(item.id) !== String(wishlistItemId) && 
          String(item._id) !== String(wishlistItemId) &&
          String(item.productId) !== String(wishlistItemId)
        );
        localStorage.setItem('wishlistItems', JSON.stringify(wishlist));
      }
      
      return { 
        success: true, 
        fromLocalStorage: true
      };
    }
  } catch (error) {
    console.log('Remove from wishlist failed:', error);
    return { success: false, message: error.message };
  }
};

export const clearWishlistAPI = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Please login to clear wishlist');
    }
    
    const countryCode = getProperCountryCode();
    
    const params = {
      countryCode: countryCode
    };
    
    console.log('Clearing wishlist with params:', params);
    
    try {
      const response = await apiCaller('delete', '/api/wishlists', null, params, { useCache: false });
      
      localStorage.setItem('wishlistItems', JSON.stringify([]));
      
      return { success: true, data: response };
    } catch (apiError) {
      console.log('Clear wishlist API failed, using localStorage:', apiError.message);
      
      localStorage.setItem('wishlistItems', JSON.stringify([]));
      
      return { 
        success: true, 
        fromLocalStorage: true
      };
    }
  } catch (error) {
    console.log('Clear wishlist failed:', error);
    return { success: false, message: error.message };
  }
};

export const checkWishlistItemAPI = async (productId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    try {
      const response = await getWishlistAPI({ skipCache: true });
      const wishlist = response?.data || [];
      
      if (!Array.isArray(wishlist)) return null;
      
      return wishlist.find(item => 
        String(item.productId) === String(productId) || 
        String(item._id) === String(productId) ||
        String(item.id) === String(productId)
      );
    } catch (error) {
      const savedWishlist = localStorage.getItem('wishlistItems');
      const wishlist = savedWishlist ? JSON.parse(savedWishlist) : [];
      
      return wishlist.find(item => 
        String(item.productId) === String(productId)
      );
    }
  } catch (error) {
    console.log('Check wishlist item failed:', error);
    return null;
  }
};

// ========== CART FUNCTIONS (unchanged) ==========

const ensureArrayAttributes = (attributes) => {
  if (!attributes) return [];
  if (Array.isArray(attributes)) return attributes;
  if (typeof attributes === 'object') {
    return Object.entries(attributes).map(([key, value]) => ({
      attributeName: key,
      value: value
    }));
  }
  if (typeof attributes === 'string') {
    try {
      const parsed = JSON.parse(attributes);
      return ensureArrayAttributes(parsed);
    } catch {
      return [{ attributeName: 'attribute', value: attributes }];
    }
  }
  return [{ attributeName: 'attribute', value: String(attributes) }];
};

export const getCartAPI = async (options = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log("No token found for cart API");
      return { success: true, data: { items: [] } };
    }
    
    const response = await apiCaller('get', '/api/cart', null, {}, options);
    
    if (response && response.success === true && response.data) {
      return response;
    } else if (response && response.data) {
      return { success: true, data: response.data };
    } else if (response && response.items) {
      return { success: true, data: response };
    } else if (Array.isArray(response)) {
      return { success: true, data: { items: response } };
    }
    
    return { success: true, data: { items: [] } };
  } catch (error) {
    console.error('Get cart error:', error);
    return { success: true, data: { items: [] } };
  }
};

export const addToCartAPI = async (productData, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Please login to add items to cart');
    }
    
    const productId = productData.productId || productData.id;
    const quantity = productData.quantity || productData.qty || 1;
    const rawAttributes = productData.selectedAttributes;
    
    const safeAttributes = ensureArrayAttributes(rawAttributes);
    
    const payload = {
      productId,
      quantity,
      selectedAttributes: safeAttributes
    };
    
    const response = await apiCaller('post', '/api/cart', payload, {}, options);
    return { success: true, data: response };
  } catch (error) {
    console.error('Add to cart error:', error);
    throw error;
  }
};

export const updateCartItemAPI = async (cartItemId, quantity, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Please login to update cart');
    }
    
    const response = await apiCaller('put', `/api/cart/${cartItemId}`, { quantity }, {}, options);
    return { success: true, data: response };
  } catch (error) {
    console.error('Update cart item error:', error);
    throw error;
  }
};

export const removeCartItemAPI = async (cartItemId, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Please login to remove items from cart');
    }
    
    const response = await apiCaller('delete', `/api/cart/${cartItemId}`, null, {}, options);
    return { success: true, data: response };
  } catch (error) {
    console.error('Remove cart item error:', error);
    throw error;
  }
};

export const clearCartAPI = async (options = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Please login to clear cart');
    }
    
    const response = await apiCaller('delete', '/api/cart', null, {}, options);
    return { success: true, data: response };
  } catch (error) {
    console.error('Clear cart error:', error);
    throw error;
  }
};

export const syncCartAPI = async (localCartItems, options = {}) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No token found for cart sync");
    
    console.log("Syncing cart with server. Local items:", localCartItems);
    
    const serverCartResponse = await getCartAPI(options);
    const serverCart = serverCartResponse?.data?.items || serverCartResponse?.items || [];
    
    const serverCartMap = {};
    serverCart.forEach(item => {
      const key = `${item.productId}-${JSON.stringify(item.selectedAttributes)}`;
      serverCartMap[key] = item;
    });
    
    const syncPromises = [];
    
    for (const item of localCartItems) {
      const productId = item.productId || item.id;
      const quantity = item.qty || item.quantity || 1;
      const rawAttributes = item.selectedAttributes;
      
      const safeAttributes = ensureArrayAttributes(rawAttributes);
      
      const key = `${productId}-${JSON.stringify(safeAttributes)}`;
      
      if (serverCartMap[key]) continue;
      
      const productData = {
        productId,
        quantity,
        selectedAttributes: safeAttributes
      };
      
      syncPromises.push(
        addToCartAPI(productData, options).catch(error => {
          console.error(`Failed to sync item ${productId}:`, error);
          return null;
        })
      );
    }
    
    if (syncPromises.length > 0) {
      await Promise.all(syncPromises);
    }
    
    const updatedResponse = await getCartAPI(options);
    return updatedResponse;
  } catch (error) {
    console.error("Error syncing cart:", error);
    throw error;
  }
};

// ========== COUNTRY & CURRENCY APIs (unchanged) ==========

export const getCountries = async (options = {}) => {
  try {
    const response = await apiCaller('get', '/api/countries', null, {}, { 
      useCache: true, 
      skipCache: false,
      ...options 
    });
    
    if (response && response.success === true && response.data) {
      return { success: true, data: response.data };
    } else if (response && response.data) {
      return { success: true, data: response.data };
    } else if (Array.isArray(response)) {
      return { success: true, data: response };
    }
    
    return { success: true, data: [] };
  } catch (error) {
    console.error('Get countries error:', error);
    return { success: false, data: [], error: error.message };
  }
};

export const getCurrencies = async (options = {}) => {
  try {
    const response = await apiCaller('get', '/api/currencies', null, {}, { 
      useCache: true, 
      skipCache: false,
      ...options 
    });
    
    if (response && response.success === true && response.data) {
      return { success: true, data: response.data };
    } else if (response && response.data) {
      return { success: true, data: response.data };
    } else if (Array.isArray(response)) {
      return { success: true, data: response };
    }
    
    return { success: true, data: [] };
  } catch (error) {
    console.error('Get currencies error:', error);
    return { success: false, data: [], error: error.message };
  }
};

export const getExchangeRates = async (options = {}) => {
  try {
    const currenciesResponse = await getCurrencies({ useCache: true, ...options });
    
    if (currenciesResponse && currenciesResponse.success && currenciesResponse.data && Array.isArray(currenciesResponse.data)) {
      const rates = {};
      currenciesResponse.data.forEach(currency => {
        if (currency && currency.code && currency.exchangeRate) {
          rates[currency.code] = currency.exchangeRate;
        }
      });
      
      return {
        success: true,
        data: rates
      };
    }
    
    return {
      success: true,
      data: {}
    };
  } catch (error) {
    console.error('Failed to fetch exchange rates:', error);
    return {
      success: false,
      data: {},
      error: error.message
    };
  }
};

export const getCountryByCode = async (code, options = {}) => {
  try {
    const response = await apiCaller('get', `/api/countries/${code}`, null, {}, options);
    
    if (response && response.success === true && response.data) {
      return { success: true, data: response.data };
    } else if (response && response.data) {
      return { success: true, data: response.data };
    }
    
    return { success: true, data: response };
  } catch (error) {
    console.error(`Get country ${code} error:`, error);
    return { success: false, data: null, error: error.message };
  }
};

export const createCountry = async (countryData) => {
  try {
    const response = await apiCaller('post', '/api/countries', countryData);
    return { success: true, data: response };
  } catch (error) {
    console.error('Create country error:', error);
    return { success: false, error: error.message };
  }
};

export const updateCountryDiscount = async (code, discountData) => {
  try {
    const response = await apiCaller('put', `/api/countries/${code}/discount`, discountData);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Update country ${code} discount error:`, error);
    return { success: false, error: error.message };
  }
};

export const getCurrencyByCode = async (code, options = {}) => {
  try {
    const response = await apiCaller('get', `/api/currencies/${code}`, null, {}, options);
    
    if (response && response.success === true && response.data) {
      return { success: true, data: response.data };
    } else if (response && response.data) {
      return { success: true, data: response.data };
    }
    
    return { success: true, data: response };
  } catch (error) {
    console.error(`Get currency ${code} error:`, error);
    return { success: false, data: null, error: error.message };
  }
};

export const createCurrency = async (currencyData) => {
  try {
    const response = await apiCaller('post', '/api/currencies', currencyData);
    return { success: true, data: response };
  } catch (error) {
    console.error('Create currency error:', error);
    return { success: false, error: error.message };
  }
};

export const updateCurrencyRate = async (code, rateData) => {
  try {
    const response = await apiCaller('put', `/api/currencies/${code}/exchange-rate`, rateData);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Update currency ${code} rate error:`, error);
    return { success: false, error: error.message };
  }
};

export const convertPrice = async (amount, fromCurrency = 'USD', toCurrency = 'INR', options = {}) => {
  try {
    const response = await apiCaller('post', '/api/currency/convert', {
      amount,
      from: fromCurrency,
      to: toCurrency
    }, {}, { useCache: false, ...options });
    
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Price conversion failed:', error);
    
    return {
      success: false,
      message: error.message || 'Conversion failed',
      data: {
        originalAmount: amount,
        convertedAmount: amount,
        from: fromCurrency,
        to: toCurrency,
        rate: 1
      }
    };
  }
};

// ========== DEFAULT EXPORT ==========
export default {
  submitContactForm,
  testContactAPI,
  registerUser,
  loginUser,
  verifyEmailToken,
  resendVerificationEmail,   
  sendMobileOtp,
  verifyMobileOtp,
  forgotPassword,
  resetPassword,
  initiatePaymentAPI,
  verifyPaymentAPI,
  getCompleteProducts,
  getBestsellerProducts,
  getBestsellerProductsByQuery,
  getProductById,
  getCompleteProduct,
  getProductSubsections,
  searchProducts,
  getCategories,
  getSubCategories,
  getOrders,
  getOrderById,
  createOrder,
  getUserProfile,
  updateUserProfile,
  getUserAddresses,
  addUserAddress,
  updateUserAddress,
  deleteUserAddress,
  getWishlistAPI,
  addToWishlistAPI,
  removeFromWishlistAPI,
  clearWishlistAPI,
  checkWishlistItemAPI,
  getCartAPI,
  addToCartAPI,
  updateCartItemAPI,
  removeCartItemAPI,
  clearCartAPI,
  syncCartAPI,
  clearCache,
  removeFromCache,
  getCountries,
  getCountryByCode,
  createCountry,
  updateCountryDiscount,
  getCurrencies,
  getCurrencyByCode,
  createCurrency,
  updateCurrencyRate,
  getExchangeRates,
  convertPrice,
};